import {
  TableColumnConfigDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  booleanFormatter,
  combineFormatters,
  dateFormatter,
  emptyArrayFormatter,
  emptyValueFormatter,
  numberFormatter,
  postfixFormatter,
} from '@asset-metrix/portal-ui';
import { ColDef } from 'ag-grid-community';

import i18n from '../../i18n/config';

import { getCellClassFunc } from './cellClasses';
import {
  CellRendererSelectorOptions,
  getCellRendererSelector,
} from './getCellRenderer';
import { getGroupFooterAggregationFunc } from './getGroupFooterAggregationFunc';

const rightAlignClasses = {
  headerClass: 'ag-right-aligned-header',
};

/* Only enable column resizing for columns that do not rely on
 * custom cell renderers like workflowVisualisation etc. */
const colTypesResizable = new Set<TableColumnConfigDto['type']>([
  'date',
  'file_name',
  'link',
  'number',
  'pageFilter',
  'text',
]);

export function configToColDef(
  col: TableColumnConfigDto,
  widgetId: WidgetDto['id'],
  isFooterLoading: boolean,
  columnsConfig: TableColumnConfigDto[],
  options?: CellRendererSelectorOptions
) {
  const fixedWidth = col.width != null;

  return {
    /* Needed to correctly resize components with fixed width for tables
     * without horizontal scrolling. In this cases, sizeColumnsToFit will be
     * used to resize the table, which potentially could cut content from
     * cell renderers like the workflowVisualisation if it is not listed here */
    suppressSizeToFit:
      fixedWidth ||
      col.type === 'file_name' ||
      col.type === 'fileDownload' ||
      col.type === 'workflowVisualization' ||
      col.type === 'workflowActions' ||
      col.type === 'workflowProgress',
    autoHeight:
      col.wordwrap ||
      (col.type === 'file_name' && options?.showFullTextSearchResults),
    field: col.key,
    headerName: col.label,
    hide:
      !!col.hidden ||
      hideColumnWithEmptyGroupedData(col, isFooterLoading, options),
    rowGroup: col.grouping != null,
    pinned: !!col.freeze,
    resizable: colTypesResizable.has(col.type),
    sortable: col.sortable,
    suppressColumnsToolPanel: !!col.hidden,
    wrapText: col.wordwrap,
    valueFormatter: getValueFormatters(col),
    cellClass: getCellClassFunc(col, options?.rowIdToHighlight),
    ...(fixedWidth ? { width: col.width } : {}),
    ...(col.minWidth != null ? { minWidth: col.minWidth } : {}),
    // ...(fixedWidth ? { minWidth: col.width } : {}),
    // ...(fixedWidth ? { maxWidth: col.width } : {}),
    ...(col.align === 'right' ? rightAlignClasses : {}),
    // aggregation options
    ...(options?.footerData &&
      'groups' in options.footerData && {
        aggFunc: getGroupFooterAggregationFunc(
          options.deepestGroupLevel,
          options.footerData.groups
        ),
      }),
    ...(col.grouping != null && { rowGroupIndex: col.grouping }),
    ...((col.type === 'workflowVisualization' ||
      col.type === 'workflowActions') && { maxWidth: 2000 }),
    cellRendererSelector: !col.hidden
      ? getCellRendererSelector(
          col,
          widgetId,
          isFooterLoading && options?.footerData?.columns?.[col.key] == null,
          columnsConfig,
          options
        )
      : undefined,
    headerComponentParams: { sublabel: col.sublabel },
  } as ColDef;
}

export const formattersByType = {
  date: [dateFormatter, emptyValueFormatter],
  file_name: [emptyValueFormatter],
  fileDownload: [emptyValueFormatter, emptyArrayFormatter],
  number: [numberFormatter, postfixFormatter, emptyValueFormatter],
  text: [postfixFormatter, booleanFormatter, emptyValueFormatter],
  link: [postfixFormatter, emptyValueFormatter],
  pageFilter: [postfixFormatter, emptyValueFormatter],
  workflowActions: [emptyArrayFormatter],
  workflowProgress: [emptyValueFormatter],
  workflowVisualization: [emptyValueFormatter],
};

const getValueFormatters = (columnConfig: TableColumnConfigDto) => {
  const formatters = formattersByType[columnConfig.type] ?? [];
  return combineFormatters(
    formatters,
    columnConfig.type === 'text'
      ? {
          trueLabel: i18n.t('app.label.true'),
          falseLabel: i18n.t('app.label.false'),
          ...columnConfig,
        }
      : columnConfig
  );
};

// Hide columns without any grouped data (footer / grouped footer)
// - Only applies if the table is a grouped table
// - Does not apply if the widget is opened in fullscreen mode
// - Does not apply if a top-level group is expanded
function hideColumnWithEmptyGroupedData(
  columnConfig: TableColumnConfigDto,
  isFooterLoading: boolean,
  options?: CellRendererSelectorOptions
) {
  const { key, appearsInFooter } = columnConfig;

  if (!options?.isGroupedTable) {
    return false;
  }

  if (options?.isFullScreenMode) {
    return false;
  }

  // some top-level group is expanded, do not hide columns then
  if (options?.isSomeTopLevelGroupExpanded) {
    return false;
  }

  if (isFooterLoading && appearsInFooter) {
    return false;
  }

  if (
    options?.footerData?.columns?.[key] == null &&
    options?.footerData?.groups?.[key] == null
  ) {
    return true;
  }

  return false;
}
